import React, { useMemo } from "react";
import type { TimesheetEntry } from "../../types/timesheet";
import { formatHours, getTimesheetStatusPill } from "../../utils/timesheetWeek";
import {
  DataTable,
  EmployeeCell,
  UtilizationCell,
  StatusPill,
  ApproveRejectActions,
  calculateUtilization,
} from "../common";
import type { TableColumn } from "../common/Table";
import type { Table2PaginationConfig } from "../common";

export interface TeamTimesheetsTableProps {
  rows: TimesheetEntry[];
  loading: boolean;
  emptyMessage: string;
  isPending: boolean;
  canActOn: (record: TimesheetEntry) => boolean;
  actionLoading: boolean;
  fullDayHours: number;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  skeleton: React.ReactNode;
  pagination?: Table2PaginationConfig;
}

const TeamTimesheetsTable: React.FC<TeamTimesheetsTableProps> = ({
  rows,
  loading,
  emptyMessage,
  isPending,
  canActOn,
  actionLoading,
  fullDayHours,
  onApprove,
  onReject,
  skeleton,
  pagination,
}) => {
  const columns: TableColumn<TimesheetEntry>[] = useMemo(() => {
    return [
      {
        key: "member",
        title: "Member",
        label: "Member",
        render: (_, record) => (
          <EmployeeCell
            name={record.fullName || "—"}
            sub={record.workEmail}
          />
        ),
      },
      {
        key: "projects",
        title: "Projects",
        label: "Projects",
        render: (_, record) => {
          const projects = Array.from(
            new Set(
              (record.tasks || [])
                .map((t) => t.project)
                .filter(Boolean) as string[],
            ),
          );
          const text =
            projects.length > 0
              ? projects.join(" · ")
              : record.project || "—";
          return (
            <div
              className="text-[12.5px] text-[var(--color-text-secondary)] truncate max-w-[200px]"
              title={text}
            >
              {text}
            </div>
          );
        },
      },
      {
        key: "logged",
        title: "Logged",
        label: "Logged",
        render: (_, record) => (
          <div className="whitespace-nowrap">
            <span className="text-[14px] font-extrabold text-[var(--color-text-primary)]">
              {formatHours(record.totalHours || 0)}
            </span>
            <span className="text-[11.5px] font-semibold text-[var(--color-text-tertiary)]">
              {" "}
              / {formatHours(fullDayHours)}
            </span>
          </div>
        ),
      },
      {
        key: "utilization",
        title: "Utilization",
        label: "Utilization",
        width: 150,
        render: (_, record) => {
          const { pct, color } = calculateUtilization(
            record.totalHours || 0,
            fullDayHours
          );
          return <UtilizationCell pct={pct} color={color} />;
        },
      },
      {
        key: "status",
        title: "Status",
        label: "Status",
        render: (_, record) => {
          const { label, bg, text, Icon } = getTimesheetStatusPill(record);
          return (
            <StatusPill
              label={label}
              bg={bg}
              text={text}
              icon={<Icon className="w-[13px] h-[13px]" />}
            />
          );
        },
      },
      {
        key: "action",
        title: "Action",
        label: "Action",
        align: "right",
        render: (_, record) => {
          if (isPending && canActOn(record)) {
            return (
              <ApproveRejectActions
                onApprove={() => onApprove(record.id)}
                onReject={() => onReject(record.id)}
                loading={actionLoading}
                size="sm"
                rejectIconOnly
                className="justify-end"
              />
            );
          }

          if (isPending) {
            return <div />;
          }

          const { label, text } = getTimesheetStatusPill(record);
          return (
            <div className="flex items-center justify-end">
              <span
                className="text-[12.5px] font-semibold"
                style={{ color: text }}
              >
                {label}
              </span>
            </div>
          );
        },
      },
    ];
  }, [
    isPending,
    canActOn,
    actionLoading,
    fullDayHours,
    onApprove,
    onReject,
  ]);

  return (
    <DataTable<TimesheetEntry>
      columns={columns}
      rows={rows}
      rowKey={(record) => record.id}
      loading={loading}
      emptyMessage={emptyMessage}
      skeleton={skeleton}
      pagination={pagination}
    />
  );
};

export default TeamTimesheetsTable;
